import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { ReplaySubject, takeUntil } from "rxjs";

import { LogService } from "@bitwarden/common/abstractions/log.service";
import { MessagingService } from "@bitwarden/common/abstractions/messaging.service";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { DeviceApprovalRequestResponse } from "@bitwarden/common/models/response/device-approval-request.response";
import { ErrorResponse } from "@bitwarden/common/models/response/error.response";

import { GlossRoutes } from "../../models/enum/routes.enum";
import { DeviceApprovalService } from "../../services/DataService/device-trust/device-approval.service";

export type DeviceApprovalScreenState =
  | "loading"
  | "waiting"
  | "unlocking"
  | "unlockFailed"
  | "denied"
  | "expired"
  | "alreadyTrusted"
  | "rateLimited"
  | "error";

// States where Request again can't help: the server would reject a new request the same way.
const FINAL_STATES: DeviceApprovalScreenState[] = ["unlockFailed", "alreadyTrusted", "rateLimited"];

/**
 * TDE new-device approval, requester side (GOOGLE_SSO_PLAN.md, Phase 3). Reached when
 * `LoginComponent` gets `response.deviceApprovalRequired: true`: an unrecognized device on an
 * account that already has a trusted device elsewhere. Asks for approval with this device's
 * public key, shows the fingerprint phrase the approving device must match, polls until the
 * request is resolved, then unlocks with the UserKey the approving device wrapped for this one.
 * See `DeviceApprovalService` for the crypto/API side.
 */
@Component({
  selector: "app-device-approval-required",
  templateUrl: "device-approval-required.component.html",
})
export class DeviceApprovalRequiredComponent implements OnInit, OnDestroy {
  state: DeviceApprovalScreenState = "loading";
  fingerprintPhrase: string;

  // Replays so a watch subscribed after destroy (the component went away while `createRequest`
  // was still in flight) is torn down immediately instead of polling forever.
  private destroy$ = new ReplaySubject<void>(1);

  constructor(
    private deviceApprovalService: DeviceApprovalService,
    private router: Router,
    private messagingService: MessagingService,
    private platformUtilsService: PlatformUtilsService,
    private logService: LogService,
  ) {}

  async ngOnInit() {
    await this.requestApproval();
  }

  get canRequestAgain(): boolean {
    return !FINAL_STATES.includes(this.state);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async requestApproval() {
    this.state = "loading";
    try {
      const request = await this.deviceApprovalService.createRequest(
        this.platformUtilsService.getDeviceString(),
      );
      this.fingerprintPhrase = await this.deviceApprovalService.getFingerprintPhrase(
        request.publicKey,
      );
      this.state = "waiting";
      this.deviceApprovalService
        .watchRequest(request)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => this.onRequestUpdate(response),
          // Polls kept failing past the request's expiry (already logged by watchRequest).
          error: () => (this.state = "expired"),
        });
    } catch (e) {
      this.logService.error(e);
      this.state = this.requestErrorState(e);
    }
  }

  private requestErrorState(e: unknown): DeviceApprovalScreenState {
    if (!(e instanceof ErrorResponse)) {
      return "error";
    }
    // "Too many device approval requests" (5 per account per hour). ErrorResponse replaces the
    // message of every 429, so the status code is the only signal.
    if (e.statusCode === 429) {
      return "rateLimited";
    }
    // A plain 400 from gloss-back once approve has inserted this device's trusted_devices row;
    // its message is the only signal. Signing in again returns this device's encryptedUserKey.
    if (e.message?.includes("already trusted")) {
      return "alreadyTrusted";
    }
    return "error";
  }

  logOut() {
    this.messagingService.send("logout");
  }

  private async onRequestUpdate(response: DeviceApprovalRequestResponse) {
    if (response.status === "approved") {
      await this.completeApproval(response);
    } else if (response.status !== "pending") {
      this.state = response.status;
    }
  }

  private async completeApproval(response: DeviceApprovalRequestResponse) {
    this.state = "unlocking";
    try {
      await this.deviceApprovalService.unlockWithApprovedRequest(response);
      // "unlocked" reconnects notifications (skipped at login time — the vault was still
      // locked then). Of LoginComponent.onSuccessfulLogin's steps only setFullWidth is
      // mirrored (no previousUrl redirect or referral): straight to Views, like
      // FirstDeviceSetupComponent.
      this.messagingService.send("unlocked");
      this.messagingService.send("setFullWidth");
      this.router.navigate([GlossRoutes.Views]);
    } catch (e) {
      this.logService.error(e);
      // Approval already made this device trusted, so a new request would be rejected.
      this.state = "unlockFailed";
    }
  }
}
